import { TableCustom } from 'app/components/elements/TableCustom';
import { HEADER_ISSUES_MANAGEMENT_VALIDATION } from 'const/room';
import React, { useMemo } from 'react';
import { AssetIssues, IOptions, IssueReport } from 'types/room';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { Link, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { lowerCase, startCase, unionBy } from 'lodash';
import { DropdownOption } from 'app/pages/RoomReportViewPhotoPage/hook';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
type TableReportTabAssetValidationProps = {
  issues: IssueReport[];
  rowActive: AssetIssues;
  mapOptions: Map<string, DropdownOption>;
};

const renderTableIssue = ({
  dataTable,
  onView,
  mapOptions,
  issueLargestOptions,
  isCanAction
}: {
  dataTable: IssueReport[];
  onView: (value: IssueReport) => void;
  issueLargestOptions: IOptions[];
  mapOptions: Map<string, DropdownOption>;
  isCanAction: boolean;
}) => {
  return dataTable.map((data) => {
    const reportIssuesOptions: Record<string, { value: JSX.Element }> = {};
    issueLargestOptions.forEach((issueOption) => {
      reportIssuesOptions[
        `${startCase(lowerCase(mapOptions.get(issueOption?.type)?.name || issueOption?.type))}`
      ] = {
        value: <></>
      };
    });
    data.options.forEach((issueOption) => {
      reportIssuesOptions[
        `${startCase(lowerCase(mapOptions.get(issueOption?.type)?.name || issueOption?.type))}`
      ] = {
        value: <TextEllipsis text={issueOption?.value} width={'95%'} />
      };
    });

    return {
      equipmentId: {
        value: <TextEllipsis text={data?.equipmentId} width={'95%'} />
      },
      ...reportIssuesOptions,
      description: {
        value: (
          <Typography whiteSpace={'pre-line'} width={'90%'}>
            {data?.description}
          </Typography>
        )
      },

      createdAt: {
        value: <TextEllipsis text={dayjs(data?.createdAt).format('YYYY-MM-DD')} width={'95%'} />
      },
      updatedAt: {
        value: <TextEllipsis text={dayjs(data?.updatedAt).format('YYYY-MM-DD')} width={'95%'} />
      },
      resolutionDate: {
        value: data?.resolutionDate ? (
          <TextEllipsis text={dayjs(data?.resolutionDate).format('YYYY-MM-DD')} width={'95%'} />
        ) : null
      },
      actionBy: {
        value: <TextEllipsis text={data?.actionBy?.replace('other_', '')} width={'95%'} />
      },
      actionByDate: {
        value: (
          <TextEllipsis
            text={data?.actionByDate ? dayjs(data?.actionByDate).format('YYYY-MM-DD') : ''}
            width={'95%'}
          />
        )
      },
      status: {
        value: <TextEllipsis text={data?.status} width={'95%'} />
      },
      action: {
        value: (
          <Stack style={{ alignItems: 'flex-start', gap: 10 }}>
            {isCanAction && (
              <Link
                style={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => {
                  onView(data);
                }}>
                View / Edit / Delete
              </Link>
            )}
          </Stack>
        )
      }
    };
  });
};
export const getOptionLabel = (
  option: IOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  singleEntryField: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  systemDropdownValues: any[]
) => {
  if (option.fieldType === 'SINGLE') {
    return singleEntryField?.find((item) => String(item.id) === option.type)?.name || option.type;
  }

  if (option.fieldType === 'DROPDOWN') {
    return (
      systemDropdownValues?.find((item) => String(item.id) === option.type)?.name || option.type
    );
  }

  return option.type;
};
export const getOptionKey = (
  option: IOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  singleEntryField: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  systemDropdownValues: any[]
) => `${startCase(lowerCase(getOptionLabel(option, singleEntryField, systemDropdownValues)))}`;
export const TableReportTabAssetValidation = ({
  issues,
  mapOptions,
  rowActive
}: TableReportTabAssetValidationProps) => {
  const { systemDropdownValues, singleEntryField } = useFilteredRoomContext();

  const navigate = useNavigate();

  const { tableHeaders, issueLargestOptions } = useMemo(() => {
    const baseHeaders = [...HEADER_ISSUES_MANAGEMENT_VALIDATION];
    let allOptions: IOptions[] = [];
    issues.forEach((item) => {
      allOptions = [...allOptions, ...item.options];
    });

    // unique theo fieldType + type
    const uniqueOptions = unionBy(allOptions, (opt) => `${opt.fieldType}_${opt.type}`);

    uniqueOptions.forEach((option, index) => {
      baseHeaders.splice(1 + index, 0, {
        label: getOptionKey(option, singleEntryField || [], systemDropdownValues || []),
        width: 150
      });
    });
    return {
      tableHeaders: baseHeaders,
      issueLargestOptions: uniqueOptions
    };
  }, [issues, singleEntryField, systemDropdownValues]);
  const { auth } = useAuth();
  const isCanAction = useMemo(
    () => (auth?.role ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role) : false),
    [auth?.role]
  );

  return (
    <Stack width={'100%'} overflow={'scroll'}>
      {issues?.length > 0 && (
        <TableCustom
          isScrollX
          data={renderTableIssue({
            dataTable: issues,
            issueLargestOptions,
            mapOptions,
            onView: (data) => {
              const url = `/clients/${rowActive?.room?.client?.clientName}/${rowActive?.room.client.id}/location/${rowActive?.room.id}/room/issues/${data.id}/view-photo`;
              navigate(url);
            },
            isCanAction
          })}
          headers={tableHeaders}
          styledTableRow={{ cursor: 'default' }}
          handleChooseRow={() => {}}
        />
      )}
    </Stack>
  );
};
